export interface VanSizeTier {
    size: number;
    rangeFrom: number;
    rangeTo: number;
    van: string;
    schema: string;
}

// Full host comes from .env (ASSETS_URL, injected via window.apiData in
// form.html.twig) rather than a relative path, so these image URLs keep
// resolving correctly regardless of where the order-form bundle is embedded.
const BASE_PATH = window.apiData.assetsUrl + 'images/vanBySize/';

// Bump this whenever the files in public/images/vanBySize are replaced
// (same filenames, new content) — otherwise browsers/CDNs keep serving the
// stale cached image instead of the update.
const CACHE_BUST = 'v=20260728';

function asset(filename: string): string {
    return `${BASE_PATH}${filename}?${CACHE_BUST}`;
}

export const VAN_BASE_IMAGE = asset('base.png');

// Reviewed from the files actually present in public/images/vanBySize —
// each size tier has a "van filled to this level" shot and a matching
// schematic. Naming is inconsistent upstream (some schemas are
// "<size>_schema.png", others "<size>m3_schema.png"), so it's mapped by hand
// here rather than guessed from a pattern.
//
// Ranges: 0-1.5, 1.5-3, 3-4.5, 4.5-6, 6-7.5, 7.5-10 — a selected size falls
// into a range by being <= its upper bound, so e.g. "Iki 5 m³" (5) lands in
// the 4.5-6 range.
export const VAN_SIZE_TIERS: VanSizeTier[] = [
    {size: 1.5, rangeFrom: 0, rangeTo: 1.5, van: asset('1.5.png'), schema: asset('1.5_schema.png')},
    {size: 3, rangeFrom: 1.5, rangeTo: 3, van: asset('3.png'), schema: asset('3m3_schema.png')},
    {size: 4.5, rangeFrom: 3, rangeTo: 4.5, van: asset('4.5.png'), schema: asset('4.5m3_schema.png')},
    {size: 6, rangeFrom: 4.5, rangeTo: 6, van: asset('6.png'), schema: asset('6m3_schema.png')},
    {size: 7.5, rangeFrom: 6, rangeTo: 7.5, van: asset('7.5.png'), schema: asset('7.5_schema.png')},
    {size: 10, rangeFrom: 7.5, rangeTo: 10, van: asset('10.png'), schema: asset('10_schema.png')},
];

// Extracts the first number out of either size format this app produces —
// manual selection gives "Iki 5 m³", AI photo analysis gives "3.42  m³".
function parseSizeValue(selectedSize: string): number | null {
    const match = selectedSize.match(/(\d+(?:[.,]\d+)?)/);
    if (!match) {
        return null;
    }
    return parseFloat(match[1].replace(',', '.'));
}

// Maps an arbitrary selected size onto its range (0-1.5, 1.5-3, ... 7.5-10).
// Sizes above 10 (AI analysis can report up to 60 m³) fall through every
// `<= rangeTo` check and land on this ?? fallback — the 10 m³ image set.
export function resolveVanSizeTier(selectedSize: string): VanSizeTier | null {
    const value = parseSizeValue(selectedSize);
    if (value === null) {
        return null;
    }

    return VAN_SIZE_TIERS.find((tier) => value <= tier.rangeTo) ?? VAN_SIZE_TIERS[VAN_SIZE_TIERS.length - 1];
}
